import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import { useAdminHeaderActions } from '../../contexts/AdminHeaderActionsContext';
import { adminDashboardTabHref } from '../../utils/adminDashboardTabs';
import ManageReviewersModal from './ManageReviewersModal';

interface TabletopExercise {
  id: number;
  exercise_name: string;
  exercise_type: string;
  scenario_description: string;
  status: string;
  scheduled_date: string | null;
  completed_date: string | null;
  admin_approved: boolean;
  iso_auditor_approved: boolean;
  document_download_enabled: boolean;
  document_url: string | null;
  uploaded_document_url: string | null;
  uploaded_document_at: string | null;
  audit_requested?: boolean;
  findings_summary?: string | null;
  cowbell_notified_at?: string | null;
  cowbell_notified_by?: number | null;
  bcp_id?: number | null;
  business_context?: Record<string, any> | null;
  tabletop_purchase_id?: number | null;
  user_id: number;
  user_email?: string;
  user_name?: string;
  insurer_name?: string | null;
  created_at: string;
  updated_at: string | null;
}

type KpiFilter = 'all' | 'needs_approval' | 'awaiting_iso' | 'completed' | 'drafts';

const TabletopManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setActions } = useAdminHeaderActions();
  const [exercises, setExercises] = useState<TabletopExercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpiFilter, setKpiFilter] = useState<KpiFilter>('all');
  const [approving, setApproving] = useState<number | null>(null);

  // Review & detail modal state
  const [reviewExercise, setReviewExercise] = useState<TabletopExercise | null>(null);
  const [isScenarioExpanded, setIsScenarioExpanded] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Approve confirmation dialog
  const [approveConfirm, setApproveConfirm] = useState<{
    open: boolean;
    exerciseId: number | null;
    exerciseName: string;
    approvalType: 'admin' | 'iso_auditor' | null;
  }>({ open: false, exerciseId: null, exerciseName: '', approvalType: null });

  // Revoke modal (replaces prompt)
  const [revokeConfirm, setRevokeConfirm] = useState<{
    open: boolean;
    exerciseId: number | null;
    exerciseName: string;
  }>({ open: false, exerciseId: null, exerciseName: '' });
  const [revokeReason, setRevokeReason] = useState('');

  // Cowbell notify state
  const [notifyingId, setNotifyingId] = useState<number | null>(null);
  const [notifyVerify, setNotifyVerify] = useState<{
    open: boolean;
    exerciseId: number | null;
    exerciseName: string;
  }>({ open: false, exerciseId: null, exerciseName: '' });
  const [notifyState, setNotifyState] = useState<{
    open: boolean;
    exerciseId: number | null;
    notes: string;
    cc_user: boolean;
    include_uploaded_doc: boolean;
  }>({ open: false, exerciseId: null, notes: '', cc_user: false, include_uploaded_doc: true });

  const [showReviewersModal, setShowReviewersModal] = useState(false);

  const urlSearch = searchParams.get('search') || '';

  useEffect(() => {
    fetchExercises();
  }, []);

  useEffect(() => {
    setActions(
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowReviewersModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
        >
          <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>Reviewers & Calendar</span>
        </button>
        <button
          type="button"
          onClick={() => navigate(adminDashboardTabHref('templates'))}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
        >
          <svg className="w-3.5 h-3.5 text-indigo-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <span>Manage Exercise Types</span>
        </button>
      </div>
    );
    return () => setActions(null);
  }, [setActions, navigate]);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      const { data } = await api.get<TabletopExercise[]>('/v1/admin/tabletop-exercises/');
      setExercises(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load tabletop exercises');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (exerciseId: number, approvalType: 'admin' | 'iso_auditor') => {
    setApproving(exerciseId);
    try {
      await api.post(`/v1/admin/tabletop-exercises/${exerciseId}/approve/`, {
        approval_type: approvalType,
        approved: true,
        comments: `Approved by admin on ${new Date().toLocaleDateString()}`
      });
      await fetchExercises();
      setApproveConfirm({ open: false, exerciseId: null, exerciseName: '', approvalType: null });
      if (reviewExercise && reviewExercise.id === exerciseId) {
        setReviewExercise((prev) =>
          prev
            ? {
                ...prev,
                [approvalType === 'admin' ? 'admin_approved' : 'iso_auditor_approved']: true,
              }
            : null
        );
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to approve exercise');
    } finally {
      setApproving(null);
    }
  };

  const handleOpenApproveConfirm = (
    exerciseId: number,
    exerciseName: string,
    approvalType: 'admin' | 'iso_auditor'
  ) => {
    setApproveConfirm({ open: true, exerciseId, exerciseName, approvalType });
  };

  const handleConfirmApprove = async () => {
    if (!approveConfirm.exerciseId || !approveConfirm.approvalType) return;
    await handleApprove(approveConfirm.exerciseId, approveConfirm.approvalType);
  };

  const handleOpenRevoke = (exerciseId: number, exerciseName: string) => {
    setRevokeReason('');
    setRevokeConfirm({ open: true, exerciseId, exerciseName });
  };

  const handleConfirmRevoke = async () => {
    if (!revokeConfirm.exerciseId) return;
    setApproving(revokeConfirm.exerciseId);
    try {
      await api.post(`/v1/admin/tabletop-exercises/${revokeConfirm.exerciseId}/reject/`, {
        reason: revokeReason || 'Approval revoked by admin'
      });
      await fetchExercises();
      if (reviewExercise && reviewExercise.id === revokeConfirm.exerciseId) {
        setReviewExercise((prev) =>
          prev
            ? {
                ...prev,
                admin_approved: false,
                iso_auditor_approved: false,
              }
            : null
        );
      }
      setRevokeConfirm({ open: false, exerciseId: null, exerciseName: '' });
      setRevokeReason('');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to revoke exercise approval');
    } finally {
      setApproving(null);
    }
  };

  const formatScheduledDate = (dateStr?: string | null) => {
    if (!dateStr) return 'Not scheduled';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatBadgeText = (text?: string | null) => {
    if (!text) return '';
    const clean = text.replace(/_/g, ' ').trim();
    return clean.charAt(0).toUpperCase() + clean.slice(1);
  };

  const handlePreviewUploaded = async (exerciseId: number) => {
    try {
      setPreviewLoading(true);
      const response = await api.get(`/v1/admin/tabletop-exercises/${exerciseId}/uploaded-document/`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError('Failed to preview document');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadUploaded = async (exerciseId: number, filename: string) => {
    try {
      const response = await api.get(`/v1/admin/tabletop-exercises/${exerciseId}/uploaded-document/`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `exercise_${exerciseId}_worksheet.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Failed to download document');
    }
  };

  const handleOpenNotifyVerify = (exerciseId: number, exerciseName: string) => {
    setNotifyVerify({ open: true, exerciseId, exerciseName });
  };

  const handleConfirmNotifyVerify = () => {
    if (!notifyVerify.exerciseId) return;
    setNotifyState({
      open: true,
      exerciseId: notifyVerify.exerciseId,
      notes: '',
      cc_user: false,
      include_uploaded_doc: true,
    });
    setNotifyVerify({ open: false, exerciseId: null, exerciseName: '' });
  };

  const handleSendNotify = async () => {
    if (!notifyState.exerciseId) return;
    setNotifyingId(notifyState.exerciseId);
    try {
      await api.post(`/v1/admin/tabletop-exercises/${notifyState.exerciseId}/notify-cowbell/`, {
        notes: notifyState.notes,
        cc_user: notifyState.cc_user,
        include_uploaded_doc: notifyState.include_uploaded_doc,
      });
      await fetchExercises();
      setNotifyState({ open: false, exerciseId: null, notes: '', cc_user: false, include_uploaded_doc: true });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to notify Cowbell');
    } finally {
      setNotifyingId(null);
    }
  };

  // KPI counts strictly matching Image 2
  const needsAdminApprovalCount = exercises.filter(
    (e) => !e.admin_approved && e.status !== 'draft'
  ).length;

  const awaitingIsoAuditCount = exercises.filter(
    (e) =>
      e.admin_approved &&
      !e.iso_auditor_approved &&
      e.status !== 'completed' &&
      (Boolean(e.audit_requested) || Boolean(e.uploaded_document_url))
  ).length;

  const completedCount = exercises.filter(
    (e) => e.status === 'completed' || (e.admin_approved && e.iso_auditor_approved)
  ).length;

  const draftCount = exercises.filter((e) => e.status === 'draft').length;

  // Filter exercises by KPI selection and search term
  const filteredExercises = exercises.filter((ex) => {
    // 1. KPI Filter
    if (kpiFilter === 'needs_approval' && (ex.admin_approved || ex.status === 'draft')) {
      return false;
    }
    if (
      kpiFilter === 'awaiting_iso' &&
      (!ex.admin_approved ||
        ex.iso_auditor_approved ||
        ex.status === 'completed' ||
        (!ex.audit_requested && !ex.uploaded_document_url))
    ) {
      return false;
    }
    if (
      kpiFilter === 'completed' &&
      ex.status !== 'completed' &&
      !(ex.admin_approved && ex.iso_auditor_approved)
    ) {
      return false;
    }
    if (kpiFilter === 'drafts' && ex.status !== 'draft') {
      return false;
    }

    // 2. Search Query Filter
    if (urlSearch.trim()) {
      const q = urlSearch.toLowerCase();
      const matchName = ex.exercise_name?.toLowerCase().includes(q);
      const matchType = ex.exercise_type?.toLowerCase().includes(q);
      const matchClient = ex.user_name?.toLowerCase().includes(q);
      const matchEmail = ex.user_email?.toLowerCase().includes(q);
      return matchName || matchType || matchClient || matchEmail;
    }

    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-300 border-t-[#991B1B]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Title & Subtitle matching Image 2 */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0F172A] tracking-tight">
          Tabletop exercises
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-1">
          Review submitted exercises, approve them and send assessment results to insurers.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 shadow-2xs flex items-center justify-between">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-xs text-red-600 underline">Dismiss</button>
        </div>
      )}

      {/* 4 Metric KPI Cards matching Image 2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Needs admin approval */}
        <div
          onClick={() => setKpiFilter(kpiFilter === 'needs_approval' ? 'all' : 'needs_approval')}
          className={`bg-white border rounded-xl p-5 shadow-2xs cursor-pointer transition-all ${
            kpiFilter === 'needs_approval'
              ? 'border-[#991B1B] ring-1 ring-[#991B1B]'
              : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <p className="text-xs font-medium text-slate-500">Needs admin approval</p>
          <p className="text-3xl font-extrabold text-slate-900 mt-2">{needsAdminApprovalCount}</p>
          <p className="text-xs text-slate-400 mt-2">Waiting on you</p>
        </div>

        {/* Card 2: Awaiting ISO assessment */}
        <div
          onClick={() => setKpiFilter(kpiFilter === 'awaiting_iso' ? 'all' : 'awaiting_iso')}
          className={`bg-white border rounded-xl p-5 shadow-2xs cursor-pointer transition-all ${
            kpiFilter === 'awaiting_iso'
              ? 'border-[#991B1B] ring-1 ring-[#991B1B]'
              : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <p className="text-xs font-medium text-slate-500">Awaiting ISO assessment</p>
          <p className="text-3xl font-extrabold text-slate-900 mt-2">{awaitingIsoAuditCount}</p>
          <p className="text-xs text-slate-400 mt-2">Admin approved</p>
        </div>

        {/* Card 3: Completed */}
        <div
          onClick={() => setKpiFilter(kpiFilter === 'completed' ? 'all' : 'completed')}
          className={`bg-white border rounded-xl p-5 shadow-2xs cursor-pointer transition-all ${
            kpiFilter === 'completed'
              ? 'border-[#991B1B] ring-1 ring-[#991B1B]'
              : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <p className="text-xs font-medium text-slate-500">Completed</p>
          <p className="text-3xl font-extrabold text-slate-900 mt-2">{completedCount}</p>
          <p className="text-xs text-slate-400 mt-2">Fully approved</p>
        </div>

        {/* Card 4: Drafts */}
        <div
          onClick={() => setKpiFilter(kpiFilter === 'drafts' ? 'all' : 'drafts')}
          className={`bg-white border rounded-xl p-5 shadow-2xs cursor-pointer transition-all ${
            kpiFilter === 'drafts'
              ? 'border-[#991B1B] ring-1 ring-[#991B1B]'
              : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <p className="text-xs font-medium text-slate-500">Drafts</p>
          <p className="text-3xl font-extrabold text-slate-900 mt-2">{draftCount}</p>
          <p className="text-xs text-slate-400 mt-2">Not submitted yet</p>
        </div>
      </div>

      {/* Main Exercises Table matching Image 2 */}
      {filteredExercises.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-xs">
          <svg className="w-12 h-12 mx-auto text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm font-medium text-slate-700">No exercises found</p>
          <p className="text-xs text-slate-400 mt-1">
            {urlSearch ? `No matches for "${urlSearch}"` : 'Try clearing your active filters'}
          </p>
          {kpiFilter !== 'all' && (
            <button
              onClick={() => setKpiFilter('all')}
              className="mt-3 text-xs font-semibold text-[#991B1B] hover:underline"
            >
              Clear filter
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/70 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-6 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    EXERCISE
                  </th>
                  <th className="py-3 px-6 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    CLIENT
                  </th>
                  <th className="py-3 px-6 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    STATUS
                  </th>
                  <th className="py-3 px-6 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    APPROVALS
                  </th>
                  <th className="py-3 px-6 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    RESULTS
                  </th>
                  <th className="py-3 px-6 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {filteredExercises.map((exercise) => {
                  const isDraft = exercise.status === 'draft';
                  const isCompleted =
                    exercise.status === 'completed' ||
                    (exercise.admin_approved && exercise.iso_auditor_approved);
                  const isAwaitingIso =
                    exercise.admin_approved &&
                    !exercise.iso_auditor_approved &&
                    (Boolean(exercise.audit_requested) || Boolean(exercise.uploaded_document_url));
                  const needsAdmin = !exercise.admin_approved && !isDraft;

                  return (
                    <tr key={exercise.id} className="hover:bg-slate-50/60 transition-colors">
                      {/* EXERCISE column */}
                      <td className="py-4 px-6">
                        <div className="flex flex-col gap-1.5">
                          <span className="font-bold text-sm text-slate-900 leading-tight">
                            {exercise.exercise_name}
                          </span>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                              {exercise.exercise_type}
                            </span>
                            {exercise.audit_requested && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                ISO 22301 assessment
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* CLIENT column */}
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="font-bold text-sm text-slate-900 leading-tight">
                            {exercise.user_name || 'Unknown'}
                          </span>
                          <span className="text-xs text-slate-500 mt-0.5">
                            {exercise.user_email || '—'}
                          </span>
                        </div>
                      </td>

                      {/* STATUS column */}
                      <td className="py-4 px-6 whitespace-nowrap">
                        {isDraft ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                            Draft
                          </span>
                        ) : isCompleted ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Completed
                          </span>
                        ) : isAwaitingIso ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            Awaiting ISO assessment
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                            Needs admin approval
                          </span>
                        )}
                      </td>

                      {/* APPROVALS column */}
                      <td className="py-4 px-6 whitespace-nowrap">
                        <div className="flex flex-col gap-1.5 text-xs font-medium">
                          {/* Admin approval indicator */}
                          <div className="flex items-center gap-2">
                            {isDraft ? (
                              <span className="w-3 h-3 rounded-full border border-slate-300 inline-block shrink-0" />
                            ) : exercise.admin_approved ? (
                              <span className="w-3 h-3 rounded-full bg-emerald-600 inline-block shrink-0" />
                            ) : (
                              <span className="w-3 h-3 rounded-full border-2 border-amber-600 inline-block shrink-0" />
                            )}
                            <span className={exercise.admin_approved ? 'text-slate-800' : 'text-slate-600'}>
                              Admin
                            </span>
                          </div>

                          {/* ISO assessor indicator (shown if assessment requested or iso approved) */}
                          {exercise.audit_requested && (
                            <div className="flex items-center gap-2">
                              {exercise.iso_auditor_approved ? (
                                <span className="w-3 h-3 rounded-full bg-emerald-600 inline-block shrink-0" />
                              ) : exercise.admin_approved ? (
                                <span className="w-3 h-3 rounded-full border-2 border-amber-600 inline-block shrink-0" />
                              ) : (
                                <span className="w-3 h-3 rounded-full border border-slate-300 inline-block shrink-0" />
                              )}
                              <span className={exercise.iso_auditor_approved ? 'text-slate-800' : 'text-slate-500'}>
                                ISO assessor
                              </span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* RESULTS column */}
                      <td className="py-4 px-6 whitespace-nowrap">
                        {exercise.uploaded_document_url ? (
                          <button
                            type="button"
                            onClick={() =>
                              handleDownloadUploaded(exercise.id, `${exercise.exercise_name}_worksheet.pdf`)
                            }
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>Worksheet</span>
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400 font-normal">Not uploaded</span>
                        )}
                      </td>

                      {/* Actions column */}
                      <td className="py-4 px-6 text-right whitespace-nowrap">
                        {needsAdmin ? (
                          <button
                            type="button"
                            onClick={() => setReviewExercise(exercise)}
                            className="px-3.5 py-1.5 bg-[#991B1B] hover:bg-[#7F1D1D] text-white text-xs font-semibold rounded-lg shadow-xs transition-colors"
                          >
                            Review
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setReviewExercise(exercise)}
                            className="px-3.5 py-1.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg shadow-2xs transition-colors"
                          >
                            View
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/40 text-xs text-slate-500">
            Showing {filteredExercises.length} exercise{filteredExercises.length === 1 ? '' : 's'}
          </div>
        </div>
      )}

      {/* Review & Details Modal */}
      {reviewExercise && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 sm:p-7 space-y-5 max-h-[92vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1.5 text-left">
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  REVIEW TABLETOP EXERCISE
                </p>
                <h3 className="text-2xl font-bold text-slate-900 leading-snug tracking-tight">
                  {reviewExercise.exercise_name}
                </h3>
                <div className="flex items-center gap-2 pt-1 flex-wrap">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                    {formatBadgeText(reviewExercise.exercise_type)}
                  </span>
                  {reviewExercise.audit_requested && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                      ISO 22301 assessment requested
                    </span>
                  )}
                  {!reviewExercise.admin_approved && reviewExercise.status !== 'draft' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                      Needs admin approval
                    </span>
                  ) : reviewExercise.admin_approved && !reviewExercise.iso_auditor_approved && reviewExercise.audit_requested ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                      Awaiting ISO assessment
                    </span>
                  ) : (reviewExercise.admin_approved && reviewExercise.iso_auditor_approved) || reviewExercise.status === 'completed' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                      Completed
                    </span>
                  ) : reviewExercise.status === 'draft' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      Draft
                    </span>
                  ) : null}
                  {reviewExercise.cowbell_notified_at && (
                    <span className="inline-flex items-center px-2.5 py-1 text-[11px] font-semibold bg-emerald-100 text-emerald-800 rounded-full">
                      Cowbell notified
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setReviewExercise(null);
                  setIsScenarioExpanded(false);
                }}
                className="w-9 h-9 rounded-xl border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 flex items-center justify-center transition-colors shrink-0"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="border-t border-slate-100" />

            {/* Client metadata */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
              <div>
                <span className="text-xs text-slate-500 font-normal block">Client</span>
                <span className="font-bold text-sm text-slate-900 block mt-0.5">
                  {reviewExercise.user_name || 'Unknown'}
                </span>
                <span className="text-xs text-slate-500 block truncate mt-0.5">
                  {reviewExercise.user_email || '—'}
                </span>
              </div>

              <div>
                <span className="text-xs text-slate-500 font-normal block">Insurer</span>
                <span className="font-bold text-sm text-slate-900 block mt-0.5">
                  {reviewExercise.insurer_name || '—'}
                </span>
              </div>

              <div>
                <span className="text-xs text-slate-500 font-normal block">Scheduled for</span>
                <span className="font-bold text-sm text-slate-900 block mt-0.5">
                  {formatScheduledDate(reviewExercise.scheduled_date)}
                </span>
              </div>
            </div>

            {/* Worksheet card */}
            {reviewExercise.uploaded_document_url && (
              <div className="border border-slate-200 rounded-xl p-3.5 sm:p-4 flex items-center justify-between gap-4 bg-white">
                <div className="flex items-center gap-3.5 min-w-0 text-left">
                  <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 font-bold text-xs flex items-center justify-center shrink-0 tracking-tight">
                    PDF
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">
                      Completed rehearsal worksheet
                    </p>
                    <p className="text-xs text-slate-500 truncate mt-0.5">
                      Uploaded by the client for assessor review
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handlePreviewUploaded(reviewExercise.id)}
                    disabled={previewLoading}
                    className="px-3.5 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors shadow-2xs disabled:opacity-50"
                  >
                    {previewLoading ? 'Loading…' : 'Preview'}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleDownloadUploaded(reviewExercise.id, `${reviewExercise.exercise_name}_worksheet.pdf`)
                    }
                    className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors shadow-2xs"
                    title="Download worksheet"
                    aria-label="Download worksheet"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Scenario */}
            {reviewExercise.scenario_description && (
              <div className="space-y-1.5 text-left">
                <h4 className="text-xs sm:text-sm font-bold text-slate-800">Scenario</h4>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  {isScenarioExpanded || reviewExercise.scenario_description.length <= 220
                    ? reviewExercise.scenario_description
                    : `${reviewExercise.scenario_description.slice(0, 220).trim()}...`}
                </p>
                {reviewExercise.scenario_description.length > 220 && (
                  <button
                    type="button"
                    onClick={() => setIsScenarioExpanded(!isScenarioExpanded)}
                    className="text-xs font-semibold text-blue-700 hover:text-blue-800 inline-flex items-center gap-1 cursor-pointer pt-0.5"
                  >
                    {isScenarioExpanded ? 'Show less ←' : 'Read full scenario →'}
                  </button>
                )}
              </div>
            )}

            {/* Findings summary */}
            {reviewExercise.findings_summary && (
              <div className="space-y-1 text-left">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Findings Summary</h4>
                <p className="text-xs text-slate-600 bg-slate-50 p-2.5 rounded-lg border border-slate-200/60 italic leading-relaxed">
                  "{reviewExercise.findings_summary}"
                </p>
              </div>
            )}

            {/* Approval path */}
            <div className="space-y-2.5 text-left">
              <h4 className="text-xs sm:text-sm font-bold text-slate-800">Approval path</h4>

              <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100 bg-white">
                {/* Step 1: Admin approval */}
                <div
                  className={`p-3.5 sm:p-4 flex items-center justify-between gap-3 transition-colors ${
                    !reviewExercise.admin_approved
                      ? 'border-l-4 border-red-700 bg-red-50/20'
                      : 'bg-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${
                        reviewExercise.admin_approved
                          ? 'bg-emerald-600 text-white'
                          : 'bg-[#B91C1C] text-white'
                      }`}
                    >
                      {reviewExercise.admin_approved ? '✓' : '1'}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900 leading-snug">Admin approval</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Check the worksheet is complete and matches the scenario.
                      </p>
                    </div>
                  </div>

                  {reviewExercise.admin_approved && (
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0">
                      Approved
                    </span>
                  )}
                </div>

                {/* Step 2: ISO 22301 assessment */}
                <div
                  className={`p-3.5 sm:p-4 flex items-center justify-between gap-3 transition-colors ${
                    reviewExercise.admin_approved && !reviewExercise.iso_auditor_approved && reviewExercise.audit_requested
                      ? 'border-l-4 border-blue-600 bg-blue-50/20'
                      : 'bg-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${
                        reviewExercise.iso_auditor_approved
                          ? 'bg-emerald-600 text-white'
                          : reviewExercise.admin_approved && reviewExercise.audit_requested
                          ? 'bg-blue-600 text-white'
                          : 'border border-slate-300 text-slate-400 bg-white'
                      }`}
                    >
                      {reviewExercise.iso_auditor_approved ? '✓' : '2'}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-800 leading-snug">ISO 22301 assessment</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Assessor confirms the response aligns with ISO 22301.
                      </p>
                    </div>
                  </div>

                  {reviewExercise.iso_auditor_approved ? (
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0">
                      Assessed
                    </span>
                  ) : !reviewExercise.admin_approved ? (
                    <span className="text-xs text-slate-400 font-medium inline-flex items-center gap-1.5 shrink-0">
                      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Waiting
                    </span>
                  ) : null}
                </div>

                {/* Step 3: Notify Cowbell */}
                <div className="p-3.5 sm:p-4 flex items-center justify-between gap-3 bg-white">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${
                        reviewExercise.cowbell_notified_at
                          ? 'bg-emerald-600 text-white'
                          : 'border border-slate-300 text-slate-400 bg-white'
                      }`}
                    >
                      {reviewExercise.cowbell_notified_at ? '✓' : '3'}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-800 leading-snug">Notify Cowbell</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Send Cowbell the assessed result for this client.
                      </p>
                    </div>
                  </div>

                  {reviewExercise.cowbell_notified_at ? (
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0">
                      Sent
                    </span>
                  ) : !reviewExercise.iso_auditor_approved && !reviewExercise.admin_approved ? (
                    <span className="text-xs text-slate-400 font-medium inline-flex items-center gap-1.5 shrink-0">
                      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Waiting
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-2 flex items-center justify-between flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate(`/tabletop-exercises/${reviewExercise.id}`)}
                className="text-xs sm:text-sm font-semibold text-slate-700 hover:text-slate-900 underline underline-offset-4 cursor-pointer"
              >
                Open full view
              </button>

              <div className="flex items-center gap-2.5">
                {(reviewExercise.admin_approved || reviewExercise.iso_auditor_approved) && (
                  <button
                    type="button"
                    onClick={() => handleOpenRevoke(reviewExercise.id, reviewExercise.exercise_name)}
                    disabled={approving === reviewExercise.id}
                    className="px-3.5 py-2 text-xs sm:text-sm font-semibold rounded-xl border border-red-200 text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    Revoke sign-off
                  </button>
                )}

                {reviewExercise.audit_requested && (
                  <button
                    type="button"
                    onClick={() => handleOpenNotifyVerify(reviewExercise.id, reviewExercise.exercise_name)}
                    disabled={notifyingId === reviewExercise.id}
                    className={`px-3.5 py-2 text-xs sm:text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 ${
                      reviewExercise.cowbell_notified_at
                        ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {notifyingId === reviewExercise.id
                      ? 'Sending…'
                      : reviewExercise.cowbell_notified_at
                      ? '✓ Notify Cowbell Again'
                      : 'Notify Cowbell'}
                  </button>
                )}

                {!reviewExercise.admin_approved ? (
                  <button
                    type="button"
                    onClick={() => handleOpenApproveConfirm(reviewExercise.id, reviewExercise.exercise_name, 'admin')}
                    disabled={approving === reviewExercise.id}
                    className="px-5 py-2.5 text-xs sm:text-sm font-semibold rounded-xl bg-[#991B1B] hover:bg-[#7F1D1D] text-white shadow-xs transition-colors disabled:opacity-50"
                  >
                    {approving === reviewExercise.id ? 'Approving…' : 'Approve as admin'}
                  </button>
                ) : !reviewExercise.iso_auditor_approved ? (
                  <button
                    type="button"
                    onClick={() =>
                      handleOpenApproveConfirm(reviewExercise.id, reviewExercise.exercise_name, 'iso_auditor')
                    }
                    disabled={approving === reviewExercise.id}
                    className="px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-xs transition-colors disabled:opacity-50"
                  >
                    {approving === reviewExercise.id ? 'Approving…' : 'Approve (ISO)'}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Confirmation Dialog */}
      {approveConfirm.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-slate-900">
              Approve {approveConfirm.approvalType === 'iso_auditor' ? 'ISO assessor' : 'admin'} sign-off?
            </h3>
            <p className="text-sm text-slate-600">
              Confirm approval for{' '}
              <span className="font-semibold text-slate-900">{approveConfirm.exerciseName}</span>.
              This records your sign-off on the exercise.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setApproveConfirm({ open: false, exerciseId: null, exerciseName: '', approvalType: null })}
                disabled={approving !== null}
                className="px-4 py-2 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmApprove}
                disabled={approving !== null}
                className={`px-4 py-2 text-xs font-semibold rounded-lg text-white disabled:opacity-50 ${
                  approveConfirm.approvalType === 'iso_auditor'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {approving !== null ? 'Approving…' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Confirmation Dialog */}
      {revokeConfirm.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Revoke Approval?</h3>
            <p className="text-sm text-slate-600">
              Are you sure you want to revoke sign-off for{' '}
              <span className="font-semibold text-slate-900">{revokeConfirm.exerciseName}</span>?
              This will reset approvals back to pending.
            </p>
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                Reason for revocation (optional)
              </label>
              <input
                type="text"
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                placeholder="e.g. Incomplete test results or documentation"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRevokeConfirm({ open: false, exerciseId: null, exerciseName: '' })}
                disabled={approving !== null}
                className="px-4 py-2 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRevoke}
                disabled={approving !== null}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
              >
                {approving !== null ? 'Revoking…' : 'Confirm Revocation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notify Cowbell Step 1 Verification */}
      {notifyVerify.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Notify Cowbell?</h3>
            <p className="text-sm text-slate-600">
              Send a Cowbell notification for{' '}
              <span className="font-semibold text-slate-900">{notifyVerify.exerciseName}</span>?
              You can add notes and attachments on the next step.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setNotifyVerify({ open: false, exerciseId: null, exerciseName: '' })}
                className="px-4 py-2 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmNotifyVerify}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-purple-600 text-white hover:bg-purple-700"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notify Cowbell Step 2 Composer */}
      {notifyState.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Notify Cowbell</h3>
            <p className="text-xs text-slate-600">
              Sends an email to the configured Cowbell address with the user, exercise details, and (optionally) the uploaded form.
            </p>
            <textarea
              placeholder="Assessor notes (e.g. summary of assessment, key gaps)..."
              value={notifyState.notes}
              onChange={(e) => setNotifyState((s) => ({ ...s, notes: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <div className="space-y-2 text-xs text-slate-700">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyState.include_uploaded_doc}
                  onChange={(e) => setNotifyState((s) => ({ ...s, include_uploaded_doc: e.target.checked }))}
                  className="rounded text-purple-600 focus:ring-purple-500"
                />
                Attach the user's uploaded form
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyState.cc_user}
                  onChange={(e) => setNotifyState((s) => ({ ...s, cc_user: e.target.checked }))}
                  className="rounded text-purple-600 focus:ring-purple-500"
                />
                CC the user
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setNotifyState((s) => ({ ...s, open: false }))}
                className="px-4 py-2 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendNotify}
                disabled={notifyingId !== null}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {notifyingId !== null ? 'Sending…' : 'Send Notification'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Manage Reviewers & Google Calendar Modal */}
      <ManageReviewersModal
        isOpen={showReviewersModal}
        onClose={() => setShowReviewersModal(false)}
      />
    </div>
  );
};

export default TabletopManagement;
